<template>
  <div class="settlement-page">
    <div class="header-area">
      <div class="header">
        <span class="title">结算记录</span>
      </div>
      <div class="stats">
        <span
          >结算总额 <strong>{{ totalAmount | formatMoney }}</strong> 元</span
        >
      </div>
    </div>

    <!-- 下拉刷新 + 列表内容（van-pull-refresh 自身滚动） -->
    <van-pull-refresh
      v-model="refreshing"
      @refresh="onRefresh"
      class="pull-refresh-wrapper"
    >
      <!-- 骨架屏 -->
      <div v-if="loading && list.length === 0" class="skeleton-wrapper">
        <div v-for="n in 3" :key="n" class="skeleton-card">
          <div class="skeleton-line w-60" />
          <div class="skeleton-line w-40" />
          <div class="skeleton-line w-80" />
          <div class="skeleton-line w-50" />
        </div>
      </div>

      <!-- 列表 -->
      <div v-else-if="list.length > 0" class="list">
        <div
          v-for="item in list"
          :key="item.sId || item.id"
          class="settlement-card"
        >
          <div class="card-header">
            <span class="project-name">{{ item.projectName }}</span>
            <span class="amount">薪酬：{{ item.salary | formatMoney }} 元</span>
          </div>
          <div class="card-body">
            <div class="info-row">
              <span class="label">发布企业</span>
              <span class="value">{{ item.company }}</span>
            </div>
            <div class="info-row">
              <span class="label">发布时间</span>
              <span class="value">{{ item.publishTime }}</span>
            </div>
            <div class="info-row">
              <span class="label">结算总额</span>
              <span class="value total-amount"
                >{{ item.totalAmount | formatMoney }} 元</span
              >
            </div>
            <div class="info-row desc">
              <span class="label">项目描述</span>
              <span class="value desc-text">{{ item.desc }}</span>
            </div>
            <div class="info-row actions">
              <button class="btn-voucher" @click="goToDetail(item)">
                凭证
              </button>
            </div>
          </div>
        </div>
        <!-- 底部哨兵元素（用于触发上拉加载） -->
        <div ref="sentinel" class="sentinel"></div>
      </div>

      <!-- 空状态 -->
      <div v-else-if="!loading && list.length === 0" class="empty-state">
        <svg viewBox="0 0 120 120" fill="none" width="120" height="120">
          <circle cx="60" cy="60" r="50" fill="#E8F1FB" />
          <rect
            x="34"
            y="36"
            width="52"
            height="48"
            rx="6"
            fill="white"
            stroke="#4A90D9"
            stroke-width="2"
          />
          <rect x="44" y="44" width="32" height="4" rx="2" fill="#D0E3F9" />
          <rect x="44" y="54" width="20" height="4" rx="2" fill="#D0E3F9" />
          <rect x="44" y="64" width="26" height="4" rx="2" fill="#D0E3F9" />
          <circle cx="74" cy="66" r="6" stroke="#B3D4F7" stroke-width="1.5" />
          <path
            d="M72 64L76 68M76 64L72 68"
            stroke="#2B7ECF"
            stroke-width="1.5"
          />
        </svg>
        <span>暂无结算记录</span>
      </div>

      <!-- 加载更多状态 -->
      <div class="load-more-status">
        <span v-if="loading && list.length > 0">
          <van-icon name="loading" size="16" /> 加载更多…
        </span>
        <span v-else-if="finished && list.length > 0">— 已全部加载 —</span>
      </div>
    </van-pull-refresh>
  </div>
</template>

<script>
import { Toast } from "vant";
import { httpPost } from "@/utils/fetch";

export default {
  name: "SettlementList",
  filters: {
    formatMoney(val) {
      if (val === null || val === undefined || val === "") return "0";
      const num = Number(val);
      if (isNaN(num)) return "0";
      return num.toLocaleString("zh-CN");
    },
  },
  data() {
    return {
      list: [],
      page: 1,
      pageSize: 10,
      total: 0,
      totalAmount: 0,
      loading: false,
      finished: false,
      refreshing: false,
      observer: null,
    };
  },
  mounted() {
    this.fetchData(true);
    this.$nextTick(() => {
      this.initObserver();
    });
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  },
  methods: {
    initObserver() {
      const sentinel = this.$refs.sentinel;
      if (!sentinel) return;
      if (this.observer) this.observer.disconnect();
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            this.loadMore();
          }
        },
        { root: null, threshold: 0.1 }
      );
      this.observer.observe(sentinel);
    },

    fetchData(refresh = false) {
      if (this.loading) return;
      this.loading = true;

      const pageNum = refresh ? 1 : this.page;
      const pageSize = this.pageSize;

      httpPost("/wechat/wechat/settList", { pageNum, pageSize })
        .then((data) => {
          this.loading = false;
          this.refreshing = false;

          if (data.code === "0000" && data.data) {
            const rows = data.data.rows || [];
            const totalAmount = data.data.count || 0;

            const formattedRows = rows.map((item) => ({
              sId: item.sId || item.id,
              projectName: item.name || "",
              company: item.merchantName || "",
              publishTime: item.createTime || "",
              salary: item.salaryRange || 0,
              totalAmount: item.salaryRange || 0,
              desc: item.describe || "",
              status: item.status,
            }));

            if (refresh) {
              this.list = formattedRows;
              this.page = 1;
            } else {
              this.list = this.list.concat(formattedRows);
              this.page = pageNum + 1;
            }
            this.total = data.data.total || 0;
            this.totalAmount = totalAmount;
            this.finished = this.list.length >= this.total;
          } else {
            if (refresh) {
              this.list = [];
              this.page = 1;
            }
            this.total = 0;
            this.totalAmount = 0;
            this.finished = true;
            Toast.fail(data.message || "获取数据失败");
          }
          this.$nextTick(() => {
            this.initObserver();
          });
        })
        .catch((error) => {
          this.loading = false;
          this.refreshing = false;
          this.list = [];
          this.page = 1;
          this.total = 0;
          this.totalAmount = 0;
          this.finished = true;
          Toast.fail("获取结算记录失败，请稍后重试");
          console.error("获取结算记录失败:", error);
          this.$nextTick(() => {
            this.initObserver();
          });
        });
    },

    onRefresh() {
      this.finished = false;
      this.fetchData(true);
    },

    loadMore() {
      if (this.loading || this.finished) return;
      this.fetchData(false);
    },

    goToDetail(item) {
      const id = item.sId || item.id;
      if (!id) {
        Toast.warning("缺少结算ID");
        return;
      }
      this.$router.push({ path: "/settlement/detail", query: { id } });
    },
  },
};
</script>

<style scoped>
/* ==================== 全局重置 ==================== */
* {
  box-sizing: border-box;
}
.settlement-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f4f7fd;
  padding: 0 16px 12px;
  max-width: 820px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  color: #1a2332;
  overflow: hidden;
}

.header-area {
  background: #ffffff;
  margin: 0 -16px 10px -16px;
  padding: 0 20px 12px 20px;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 2px 12px rgba(0, 20, 50, 0.06);
  flex-shrink: 0;
}

.header {
  padding: 18px 0 4px 0;
}
.header .title {
  font-size: 24px;
  font-weight: 700;
  background: linear-gradient(135deg, #1989fa 0%, #07c160 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: inline-block;
}

.stats {
  padding: 4px 0 6px 0;
}
.stats span {
  background: #f5f7fa;
  border-radius: 20px;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  color: #7a88a6;
  border: 1px solid #e8ecf2;
}
.stats strong {
  color: #e74c5e;
  font-size: 28px;
  margin: 0 4px;
  font-weight: 700;
}

.pull-refresh-wrapper {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  min-height: 0;
}

.skeleton-wrapper {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 4px;
}
.skeleton-card {
  background: #fff;
  border-radius: 16px;
  padding: 18px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.skeleton-line {
  height: 14px;
  background: #eef1f5;
  border-radius: 8px;
  animation: pulse 1.2s ease-in-out infinite;
}
.skeleton-line.w-60 {
  width: 60%;
}
.skeleton-line.w-40 {
  width: 40%;
}
.skeleton-line.w-80 {
  width: 80%;
}
.skeleton-line.w-50 {
  width: 50%;
}
@keyframes pulse {
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.6;
  }
}

.list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 10px;
  padding-bottom: 100px;
}

.settlement-card {
  background: #ffffff;
  border-radius: 18px;
  padding: 18px 20px 16px;
  box-shadow: 0 2px 12px rgba(0, 20, 50, 0.04);
  transition: box-shadow 0.25s, transform 0.15s;
}
.settlement-card:active {
  transform: scale(0.99);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.project-name {
  font-size: 17px;
  font-weight: 600;
  color: #0b1a33;
}

.amount {
  font-size: 14px;
  color: #e74c5e;
  font-weight: 600;
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.info-row {
  display: flex;
  font-size: 14px;
  line-height: 1.6;
}
.info-row .label {
  width: 76px;
  flex-shrink: 0;
  color: #7a88a6;
}
.info-row .value {
  color: #1a2332;
}
.total-amount {
  color: #e74c5e;
  font-weight: 600;
}

.desc {
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px dashed #e8ecf2;
}
.desc-text {
  color: #5a6a82;
  word-break: break-all;
}

.actions {
  margin-top: 8px;
  justify-content: flex-end;
}
.btn-voucher {
  background: transparent;
  border: 1px solid #4f7cff;
  color: #4f7cff;
  font-size: 13px;
  font-weight: 500;
  padding: 4px 18px;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.2s;
  background: #f5f9ff;
}
.btn-voucher:active {
  transform: scale(0.95);
  background: #e6efff;
}

.sentinel {
  height: 1px;
  margin-top: -1px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: 16px;
  color: #8a99b0;
  font-size: 16px;
}
.empty-state .van-icon {
  opacity: 0.5;
  margin-bottom: 8px;
}

.load-more-status {
  text-align: center;
  font-size: 13px;
  color: #a0abb9;
  padding: 12px 0 8px;
}

@media (max-width: 480px) {
  .settlement-page {
    padding: 0 10px 10px;
  }
  .header-area {
    margin: 0 -10px 0 -10px;
    padding: 0 16px 12px 16px;
  }
  .header {
    padding: 14px 0 2px 0;
  }
  .header .title {
    font-size: 20px;
  }
  .stats span {
    font-size: 14px;
    padding: 6px 14px;
  }
  .stats strong {
    font-size: 24px;
  }
  .settlement-card {
    padding: 14px 16px 12px;
  }
  .project-name {
    font-size: 15px;
  }
  .info-row {
    font-size: 13px;
  }
  .label {
    width: 68px;
  }
}
</style>